import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loading, EmptyState } from "@/components/common";
import { CONSIDERATION_STATE, VOTE_META, fmtTime, downloadForumFile } from "@/lib/forumApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronLeft, ChevronRight, FileText, Download, CheckCircle2,
  AlertTriangle, Clock, Landmark, ListChecks, History,
} from "lucide-react";

export default function ForumItem() {
  const { id, itemId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [order, setOrder] = useState([]);
  const [err, setErr] = useState(false);
  const [selected, setSelected] = useState(null);      // pending choice (pre-confirm)
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const prevState = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/forum/meetings/${id}/items/${itemId}`);
      // live-state announcements
      const st = r.data.consideration?.status;
      if (prevState.current && prevState.current !== st) {
        if (st === "voting_open") toast.info("Voting is now open for this item.");
        if (st === "voting_closed") toast.info("Voting has closed for this item.");
      }
      prevState.current = st;
      setData(r.data);
    } catch { setErr(true); }
  }, [id, itemId]);

  useEffect(() => {
    api.get(`/forum/meetings/${id}`).then((r) => setOrder(r.data.agenda.map((a) => a.item_id))).catch(() => {});
  }, [id]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  if (err) return <EmptyState icon={Landmark} title="Item not available" description="You may not have access to this item." />;
  if (!data) return <Loading label="Loading item…" />;

  const it = data.item;
  const cons = data.consideration || {};
  const st = cons.status || "scheduled";
  const myVote = data.my_vote;
  const results = data.results;
  const idx = order.indexOf(itemId);
  const prevId = idx > 0 ? order[idx - 1] : null;
  const nextId = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;

  const submitVote = async () => {
    setSubmitting(true); setFailed(false);
    try {
      const r = await api.post(`/forum/considerations/${cons.consideration_id}/vote`, { vote_value: selected });
      if (!r.data?.recorded) throw new Error("not recorded");
      setSelected(null);
      await load();
      toast.success("Vote recorded");
    } catch (e) {
      setFailed(true);
      const msg = formatApiError(e.response?.data?.detail);
      if (e.response?.status === 409) { toast.error(msg || "Voting has closed."); setSelected(null); await load(); }
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="forum-item" className="space-y-6">
      {/* nav */}
      <div className="flex items-center justify-between gap-2">
        <Link to={`/forum/meetings/${id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-brand-navy">
          <ArrowLeft className="h-4 w-4" /> Agenda
        </Link>
        <div className="flex items-center gap-2">
          <button data-testid="forum-prev-item" disabled={!prevId} onClick={() => navigate(`/forum/meetings/${id}/items/${prevId}`)}
            className="inline-flex items-center gap-1 rounded-lg border border-brand-line bg-white px-3 py-2 text-sm font-semibold text-brand-navy disabled:opacity-40 hover:bg-brand-bg">
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <button data-testid="forum-next-item" disabled={!nextId} onClick={() => navigate(`/forum/meetings/${id}/items/${nextId}`)}
            className="inline-flex items-center gap-1 rounded-lg border border-brand-line bg-white px-3 py-2 text-sm font-semibold text-brand-navy disabled:opacity-40 hover:bg-brand-bg">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* item header */}
      <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-card">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-civic">{it.category_label} · Item {it.number}</span>
          {it.is_current && <span className="inline-flex items-center rounded-full bg-brand-gold px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#5c4405]">Current</span>}
        </div>
        <h1 className="font-display text-2xl font-extrabold text-brand-ink">{it.title}</h1>
        {it.summary && <p className="text-slate-600 mt-3 leading-relaxed">{it.summary}</p>}
        {it.content && Object.keys(it.content).length > 0 && (
          <div className="mt-4 space-y-3">
            {Object.entries(it.content).map(([k, v]) => v ? (
              <div key={k}>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{k.replace(/_/g, " ")}</div>
                <p className="text-sm text-brand-ink mt-0.5 whitespace-pre-wrap leading-relaxed">{String(v)}</p>
              </div>
            ) : null)}
          </div>
        )}
      </div>

      {/* voting panel */}
      <VotePanel st={st} myVote={myVote} results={results} cons={cons}
        selected={selected} setSelected={setSelected} submitting={submitting}
        failed={failed} onConfirm={submitVote} onRetry={submitVote} />

      {/* documents */}
      {it.attachments?.length > 0 && (
        <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-card">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-civic mb-3"><ListChecks className="h-4 w-4" /> Documents to Review</h2>
          <div className="space-y-2" data-testid="forum-attachments">
            {it.attachments.map((a) => (
              <button key={a.attachment_id} data-testid="forum-attachment"
                onClick={() => downloadForumFile(id, itemId, a).catch(() => toast.error("Could not open document"))}
                className="w-full flex items-center gap-3 rounded-xl border border-brand-line px-4 py-3 text-left hover:border-brand-civic/40 hover:bg-brand-bg transition-colors">
                <FileText className="h-5 w-5 text-brand-civic shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-brand-ink truncate">{a.title}</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">{a.doc_type} · v{a.version} · {a.visibility === "public" ? "Public" : "Forum"}</div>
                </div>
                <Download className="h-4 w-4 text-slate-400 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* prior decisions */}
      {data.decision_history?.length > 0 && (
        <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-card">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-500 mb-3"><History className="h-4 w-4" /> Prior Considerations</h2>
          <div className="space-y-2">
            {data.decision_history.map((h, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-brand-line px-3 py-2 text-sm">
                <span className="text-brand-ink">{h.governing_body_name} · {h.meeting_date}</span>
                <span className="font-semibold text-brand-teal">{h.official_result_label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VotePanel({ st, myVote, results, cons, selected, setSelected, submitting, failed, onConfirm, onRetry }) {
  // Already voted (locked)
  if (myVote) {
    return (
      <div data-testid="vote-receipt" role="status" aria-live="polite" className="rounded-2xl border border-brand-teal/40 bg-brand-teal-50 p-6 text-center">
        <CheckCircle2 className="h-9 w-9 text-brand-teal mx-auto" />
        <div className="font-display text-lg font-extrabold text-brand-ink mt-2">Vote Recorded</div>
        <div className="mt-1 text-slate-600">Your vote: <span className="font-bold text-brand-ink">{VOTE_META[myVote.vote_value]?.label || myVote.vote_value}</span></div>
        <div className="text-xs text-slate-500 mt-0.5">Recorded at {fmtTime(myVote.submitted_at)}</div>
        <p className="text-xs text-slate-500 mt-3">Your vote is locked and cannot be changed.</p>
        {(st === "voting_closed" || st === "completed") && results && <ResultsBlock results={results} cons={cons} />}
      </div>
    );
  }
  // Voting open — choose + confirm
  if (st === "voting_open") {
    return (
      <div data-testid="vote-panel" className="rounded-2xl border border-brand-gold/50 bg-white p-6 shadow-card">
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold-50 border border-brand-gold/40 px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#9A7212]">Voting is Open</span>
          <p className="text-sm text-slate-500 mt-2">Cast your vote. You will confirm before it is recorded.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="group" aria-label="Cast your vote: Yes, No, or Withheld">
          {["YES", "NO", "WITHHELD"].map((v) => (
            <button key={v} data-testid={`vote-${v.toLowerCase()}`} onClick={() => setSelected(v)}
              aria-pressed={selected === v} aria-label={`Vote ${VOTE_META[v].label}`}
              className={`h-20 rounded-2xl border-2 text-lg font-extrabold tracking-wide transition-all ${VOTE_META[v].cls} hover:scale-[1.02] bg-white`}>
              {VOTE_META[v].label.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="sr-only" role="status" aria-live="polite">
          {selected ? `You selected ${VOTE_META[selected].label}. A confirmation is required before your vote is recorded.` : ""}
        </div>

        <Dialog open={!!selected} onOpenChange={(o) => { if (!o && !submitting) setSelected(null); }}>
          <DialogContent data-testid="vote-confirm-dialog">
            <DialogHeader><DialogTitle>Confirm Your Vote</DialogTitle>
              <DialogDescription>Your selected vote is shown below. Once confirmed it cannot be changed.</DialogDescription>
            </DialogHeader>
            <div className="text-center py-2">
              <p className="text-sm text-slate-500">You selected</p>
              <div className={`mt-2 inline-flex items-center justify-center h-16 min-w-[9rem] rounded-2xl border-2 text-2xl font-extrabold ${selected ? VOTE_META[selected].sel : ""}`}>
                {selected ? VOTE_META[selected].label.toUpperCase() : ""}
              </div>
              <p className="text-sm text-slate-600 mt-4">Once confirmed, your vote <strong>cannot be changed</strong>.</p>
              {failed && (
                <div data-testid="vote-failed" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span><strong>Vote Not Submitted.</strong> We could not confirm your vote with GovFlowState.</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <button onClick={() => setSelected(null)} disabled={submitting}
                className="rounded-lg border border-brand-line px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-brand-bg disabled:opacity-40">Go Back</button>
              <button data-testid="vote-confirm-btn" onClick={failed ? onRetry : onConfirm} disabled={submitting}
                className="rounded-lg bg-brand-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-navy-900 disabled:opacity-40">
                {submitting ? "Submitting…" : failed ? "Try Again" : `Confirm ${selected ? VOTE_META[selected].label.toUpperCase() : ""}`}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  // Closed / completed with results (member didn't vote)
  if ((st === "voting_closed" || st === "completed") && results) {
    return (
      <div data-testid="vote-results" className="rounded-2xl border border-brand-line bg-white p-6 shadow-card">
        <div className="font-display text-lg font-extrabold text-brand-ink text-center mb-3">Voting Complete</div>
        <ResultsBlock results={results} cons={cons} />
      </div>
    );
  }
  // Pre-vote states
  const closed = st === "voting_closed" || st === "completed" || st === "continued";
  return (
    <div data-testid="vote-prevote" className="rounded-2xl border border-brand-line bg-white p-6 shadow-card text-center">
      {closed ? <Clock className="h-8 w-8 text-slate-400 mx-auto" /> : <Clock className="h-8 w-8 text-brand-civic mx-auto" />}
      <div className="font-display text-lg font-extrabold text-brand-ink mt-2">
        {st === "under_discussion" ? "Discussion in Progress" : closed ? "Voting Has Closed" : "Waiting for Vote"}
      </div>
      <p className="text-sm text-slate-500 mt-1">
        {st === "under_discussion" ? "Voting has not opened for this item yet."
          : closed ? "The clerk has closed voting for this item."
            : "The clerk will open voting when discussion is complete."}
      </p>
    </div>
  );
}

function ResultsBlock({ results, cons }) {
  return (
    <div className="mt-4">
      <div className="grid grid-cols-3 gap-3">
        {["YES", "NO", "WITHHELD"].map((v) => (
          <div key={v} className="rounded-xl border border-brand-line bg-brand-bg p-4 text-center">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{VOTE_META[v].label}</div>
            <div className="font-display text-3xl font-extrabold text-brand-navy mt-1">{results[v] ?? 0}</div>
          </div>
        ))}
      </div>
      {cons.official_result_label && (
        <div className="mt-4 text-center">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Official Result</div>
          <div className="font-display text-2xl font-extrabold text-brand-teal mt-0.5">{cons.official_result_label}</div>
        </div>
      )}
    </div>
  );
}
